import useJumboFooterTheme from "@jumbo/hooks/useJumboFooterTheme";
import useJumboHeaderTheme from "@jumbo/hooks/useJumboHeaderTheme";
import useJumboLayoutSidebar from "@jumbo/hooks/useJumboLayoutSidebar";
import useJumboSidebarTheme from "@jumbo/hooks/useJumboSidebarTheme";
import useJumboTheme from "@jumbo/hooks/useJumboTheme";
import { SIDEBAR_STYLES, SIDEBAR_VARIANTS } from "@jumbo/utils/constants";
import { HomeOutlined } from "@mui/icons-material";
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import NotificationsIcon from '@mui/icons-material/Notifications';
import SearchIcon from '@mui/icons-material/Search';
import { Chip, Divider, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, useMediaQuery } from "@mui/material";
import Stack from "@mui/material/Stack";
import { alpha } from "@mui/material/styles";
import { getObjectListApi } from 'api/general';
import { applyFontScaleToLayers, getStoredFontScale, storeFontScale } from 'app/themes/fontScale';
import { isRoleCheckList } from 'app/utils/appHelpers';
import { ANNOUNCEMENT } from 'app/utils/constants/customConstant';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logoutUser, reset } from 'stores/auth';
import Logo from "../../../../shared/Logo";
import AuthUserDropdown from "../../../../shared/widgets/AuthUserDropdown";


const Header = () => {
    const { sidebarOptions, setSidebarOptions } = useJumboLayoutSidebar();
    const { headerTheme, setHeaderTheme } = useJumboHeaderTheme();
    const { theme, setTheme } = useJumboTheme();
    const { sidebarTheme, setSidebarTheme } = useJumboSidebarTheme();
    const { footerTheme, setFooterTheme } = useJumboFooterTheme();
    const dispatch = useDispatch();
    const [fontScale, setFontScale] = useState(getStoredFontScale);
    const authUser = useSelector((state) => state.auth.authUser);
    const allAuthorizedInsts = useSelector(state => state.auth.allAuthorizedInsts);
    const [announcements, setAnnouncements] = useState([]);
    const navigate = useNavigate();
    const isDarkHeader = headerTheme?.type === 'dark';

    useEffect(() => {
        if (!authUser) return;

        const getAPI = async () => {
            const res = await getObjectListApi(ANNOUNCEMENT);
            setAnnouncements(res.data);
        };
        getAPI();
    }, [authUser]);

    const getVisibleAnnouncementsCount = () => {
        if (announcements.length === 0) return 0;

        let visibleAnnouncements = announcements.filter(x => x.status === 1);

        if (allAuthorizedInsts && allAuthorizedInsts.length > 0) {
            visibleAnnouncements = visibleAnnouncements.filter(x => {
                const toAnnouncementArray = x.toAnnouncement ? x.toAnnouncement.split(',').map(item => item.trim()) : [];
                return toAnnouncementArray.some(inst => allAuthorizedInsts.includes(inst));
            });
        }

        return visibleAnnouncements.length;
    };

    const handleFontSizeChange = (event, newSize) => {
        if (newSize === null) return;
        // Kademeyi kaydet ve Jumbo ana/header/sidebar/footer temalarına ölçü yaması bas.
        setFontScale(newSize);
        storeFontScale(newSize);
        applyFontScaleToLayers({
            theme,
            setTheme,
            headerTheme,
            setHeaderTheme,
            sidebarTheme,
            setSidebarTheme,
            footerTheme,
            setFooterTheme,
        }, newSize);
    };

    const onLogout = () => {
        dispatch(logoutUser());
        dispatch(reset());
    };

    const showDropdownSearch = useMediaQuery('(max-width:575px)');

    const getDetailSearchClick = () => {
        navigate('/detailSearchPage');
    };

    const handleAnnouncementsClick = () => {
        navigate('/listAnnouncement');
    };

    const actionIconSx = {
        width: 36,
        height: 36,
        color: 'inherit',
        backgroundColor: isDarkHeader ? alpha('#fff', 0.12) : alpha('#000', 0.04),
        '&:hover': {
            backgroundColor: isDarkHeader ? alpha('#fff', 0.2) : alpha('#000', 0.08),
        },
    };

    const fontToggleGroupSx = {
        height: 32,
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${isDarkHeader ? alpha('#fff', 0.2) : 'divider'}`,
        backgroundColor: isDarkHeader ? alpha('#fff', 0.06) : alpha('#000', 0.02),
        '& .MuiToggleButtonGroup-grouped': {
            border: 0,
            borderRadius: 0,
            mx: 0,
            minWidth: 34,
            px: 1,
            '&:not(:first-of-type)': {
                borderLeft: `1px solid ${isDarkHeader ? alpha('#fff', 0.2) : 'divider'}`,
            },
        },
    };

    const fontToggleSx = {
        color: 'inherit',
        '&.Mui-selected': {
            backgroundColor: isDarkHeader ? alpha('#fff', 0.22) : alpha('#7352C7', 0.12),
            color: isDarkHeader ? '#fff' : 'primary.main',
            '&:hover': {
                backgroundColor: isDarkHeader ? alpha('#fff', 0.28) : alpha('#7352C7', 0.18),
            },
        },
        '&:hover': {
            backgroundColor: isDarkHeader ? alpha('#fff', 0.1) : alpha('#7352C7', 0.06),
        },
    };

    const dividerSx = {
        alignSelf: 'center',
        height: 22,
        borderColor: isDarkHeader ? alpha('#fff', 0.2) : 'divider',
        mx: 0.25,
    };

    return (
        <React.Fragment>
            {
                (
                    sidebarOptions.style === SIDEBAR_STYLES.CLIPPED_UNDER_HEADER
                    || sidebarOptions.variant === SIDEBAR_VARIANTS.TEMPORARY
                    || (sidebarOptions.variant === SIDEBAR_VARIANTS.PERSISTENT && !sidebarOptions.open)
                ) &&
                <IconButton
                    edge="start"
                    color="inherit"
                    aria-label="open drawer"
                    sx={{
                        ml: sidebarOptions.style === SIDEBAR_STYLES.CLIPPED_UNDER_HEADER ? -2 : 0,
                        mr: 2,
                    }}
                    onClick={() => setSidebarOptions({ open: !sidebarOptions.open })}
                >
                    {
                        sidebarOptions?.open ? <MenuOpenIcon /> : <MenuIcon />
                    }
                </IconButton>
            }
            {
                sidebarOptions?.style === SIDEBAR_STYLES.CLIPPED_UNDER_HEADER &&
                <Logo sx={{ mr: 2 }} mode={headerTheme.type ?? "light"} />
            }

            <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ width: '100%', minWidth: 0, gap: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
                    {/* Yazı boyutu */}
                    <ToggleButtonGroup
                        value={fontScale}
                        exclusive
                        onChange={handleFontSizeChange}
                        aria-label="yazı boyutu"
                        size="small"
                        sx={fontToggleGroupSx}
                    >
                        <ToggleButton value="small" sx={fontToggleSx} aria-label="küçük yazı">
                            <Tooltip title="Küçük yazı boyutu">
                                <span style={{ fontSize: 10, fontWeight: 700, lineHeight: 1 }}>A</span>
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="medium" sx={fontToggleSx} aria-label="orta yazı">
                            <Tooltip title="Orta yazı boyutu">
                                <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1 }}>A</span>
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="large" sx={fontToggleSx} aria-label="büyük yazı">
                            <Tooltip title="Büyük yazı boyutu">
                                <span style={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>A</span>
                            </Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Divider orientation="vertical" flexItem sx={dividerSx} />

                    {/* Arama / Duyuru / Ana sayfa */}
                    {authUser !== null && (isRoleCheckList(authUser.userRoles, [1, 373, 368, 369, 370, 371, 372, 418, 375])) ? (
                        <Tooltip title={showDropdownSearch ? "Ara" : "Detaylı arama"} placement="bottom">
                            <IconButton
                                onClick={getDetailSearchClick}
                                aria-label="detaylı arama"
                                size="small"
                                sx={actionIconSx}
                            >
                                <SearchIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ) : null}

                    {authUser !== null && authUser &&
                        authUser.userRoles &&
                        authUser.userRoles.some(role => [1, 370, 372, 373, 369, 416, 417].includes(role.id)) && (
                            <Tooltip title="Duyurular" placement="bottom">
                                <IconButton
                                    onClick={handleAnnouncementsClick}
                                    aria-label="duyurular"
                                    size="small"
                                    sx={{ ...actionIconSx, position: 'relative' }}
                                >
                                    <NotificationsIcon fontSize="small" />
                                    {getVisibleAnnouncementsCount() > 0 && (
                                        <Chip
                                            label={getVisibleAnnouncementsCount()}
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: -4,
                                                right: -4,
                                                height: 16,
                                                minWidth: 16,
                                                fontSize: 10,
                                                fontWeight: 600,
                                                backgroundColor: 'error.main',
                                                color: '#fff',
                                                '& .MuiChip-label': {
                                                    px: 0.5,
                                                },
                                            }}
                                        />
                                    )}
                                </IconButton>
                            </Tooltip>
                        )}

                    <Tooltip title="Ana sayfa" placement="bottom">
                        <IconButton
                            onClick={() => navigate("/")}
                            aria-label="ana sayfa"
                            size="small"
                            sx={actionIconSx}
                        >
                            <HomeOutlined fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Divider orientation="vertical" flexItem sx={dividerSx} />

                    <Tooltip title="BBS'ye Dön" placement="top">
                        <IconButton
                            onClick={onLogout}
                            aria-label="BBS'ye dön"
                            size="small"
                            sx={actionIconSx}
                        >
                            <LogoutIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    {authUser !== null && authUser && (
                        <AuthUserDropdown />
                    )}
                </Stack>
            </Stack>
        </React.Fragment>
    );
};

export default Header;
